// Enhanced Organizational Data Service with IndexedDB Integration
// Provides comprehensive data management for user metrics and materials

import { indexedDBService } from './indexedDBService';
import { UserMetricData, UserMaterialData } from '@/types/unifiedLibrary';

interface CacheEntry<T> {
  data: T[];
  timestamp: number;
  userId: string;
}

class EnhancedOrganizationalDataService {
  private static instance: EnhancedOrganizationalDataService;
  private metricsCache: Map<string, CacheEntry<UserMetricData>> = new Map();
  private materialsCache: Map<string, CacheEntry<UserMaterialData>> = new Map();
  private cacheExpiryTime = 5 * 60 * 1000; // 5 minutes
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): EnhancedOrganizationalDataService {
    if (!EnhancedOrganizationalDataService.instance) {
      EnhancedOrganizationalDataService.instance = new EnhancedOrganizationalDataService();
    }
    return EnhancedOrganizationalDataService.instance;
  }

  async initialize(): Promise<{ success: boolean; error?: string }> {
    try {
      if (this.isInitialized) {
        return { success: true };
      }

      const result = await indexedDBService.initialize();
      if (result.success) {
        this.isInitialized = true;
      }
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initialize service'
      };
    }
  }

  private isCacheValid(cacheEntry: CacheEntry<any>): boolean {
    return Date.now() - cacheEntry.timestamp < this.cacheExpiryTime;
  }

  private getCacheKey(userId: string, dataType: 'metrics' | 'materials'): string {
    return `${userId}_${dataType}`;
  }

  private setCache<T>(userId: string, dataType: 'metrics' | 'materials', data: T[]): void {
    const cacheKey = this.getCacheKey(userId, dataType);
    const cache = dataType === 'metrics' ? this.metricsCache : this.materialsCache;
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      userId
    });
  }

  private getCache<T>(userId: string, dataType: 'metrics' | 'materials'): T[] | null {
    const cacheKey = this.getCacheKey(userId, dataType);
    const cache = dataType === 'metrics' ? this.metricsCache : this.materialsCache;
    const cacheEntry = cache.get(cacheKey);

    if (cacheEntry && cacheEntry.userId === userId && this.isCacheValid(cacheEntry)) {
      return cacheEntry.data as T[];
    }

    return null;
  }

  private clearCache(userId: string, dataType?: 'metrics' | 'materials'): void {
    if (dataType) {
      const cacheKey = this.getCacheKey(userId, dataType);
      const cache = dataType === 'metrics' ? this.metricsCache : this.materialsCache;
      cache.delete(cacheKey);
    } else {
      // Clear all cache for this user
      this.metricsCache.delete(this.getCacheKey(userId, 'metrics'));
      this.materialsCache.delete(this.getCacheKey(userId, 'materials'));
    }
  }

  async getUserMetrics(userId: string): Promise<{ success: boolean; metrics?: UserMetricData[]; error?: string }> {
    try {
      // Check cache first
      const cachedData = this.getCache<UserMetricData>(userId, 'metrics');
      if (cachedData) {
        return { success: true, metrics: cachedData };
      }

      // If not in cache, load from IndexedDB
      // Note: We need to transform the data to match the expected UserMetricData format
      const result = await indexedDBService.getAll('userMetrics', 'userId', userId);
      if (result.success) {
        const rawMetrics = result.data as any[];
        // Transform the data to match UserMetricData interface
        const metrics: UserMetricData[] = rawMetrics.map(rawMetric => ({
          id: rawMetric.id,
          organization_id: rawMetric.organization_id || rawMetric.userId,
          metric_id: rawMetric.metric_id,
          name: rawMetric.name,
          category: rawMetric.category,
          subcategory: rawMetric.subcategory,
          description: rawMetric.description,
          unit: rawMetric.unit,
          currency: rawMetric.currency,
          custom_value: rawMetric.custom_value,
          custom_metadata: rawMetric.custom_metadata,
          is_custom: rawMetric.is_custom,
          created_at: rawMetric.created_at,
          updated_at: rawMetric.updated_at
        }));
        this.setCache(userId, 'metrics', metrics);
        return { success: true, metrics };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get user metrics'
      };
    }
  }

  async getUserMaterials(userId: string): Promise<{ success: boolean; materials?: UserMaterialData[]; error?: string }> {
    try {
      // Check cache first
      const cachedData = this.getCache<UserMaterialData>(userId, 'materials');
      if (cachedData) {
        return { success: true, materials: cachedData };
      }

      // If not in cache, load from IndexedDB
      // Note: We need to transform the data to match the expected UserMaterialData format
      const result = await indexedDBService.getAll('userMaterials', 'userId', userId);
      if (result.success) {
        const rawMaterials = result.data as any[];
        // Transform the data to match UserMaterialData interface
        const materials: UserMaterialData[] = rawMaterials.map(rawMaterial => ({
          id: rawMaterial.id,
          organization_id: rawMaterial.organization_id || rawMaterial.userId,
          material_id: rawMaterial.material_id,
          name: rawMaterial.name,
          category: rawMaterial.category,
          subcategory: rawMaterial.subcategory,
          description: rawMaterial.description,
          unit: rawMaterial.unit,
          density: rawMaterial.density,
          carbon_footprint: rawMaterial.carbon_footprint,
          cost_per_unit: rawMaterial.cost_per_unit,
          currency: rawMaterial.currency,
          custom_density: rawMaterial.custom_density,
          custom_carbon_footprint: rawMaterial.custom_carbon_footprint,
          custom_cost_per_unit: rawMaterial.custom_cost_per_unit,
          custom_metadata: rawMaterial.custom_metadata,
          is_custom: rawMaterial.is_custom,
          created_at: rawMaterial.created_at,
          updated_at: rawMaterial.updated_at
        }));
        this.setCache(userId, 'materials', materials);
        return { success: true, materials };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get user materials'
      };
    }
  }

  async createUserMetric(userId: string, metric: Omit<UserMetricData, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; metric?: UserMetricData; error?: string }> {
    try {
      const newMetric: UserMetricData = {
        ...metric,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Transform to match IndexedDB schema (add userId field for indexing)
      const indexedDBMetric = {
        ...newMetric,
        userId: userId // Add this for the IndexedDB index
      };

      const result = await indexedDBService.add('userMetrics', indexedDBMetric);
      
      if (result.success) {
        // Clear cache to force refresh
        this.clearCache(userId, 'metrics');
        return { success: true, metric: newMetric };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create user metric'
      };
    }
  }

  async createUserMaterial(userId: string, material: Omit<UserMaterialData, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; material?: UserMaterialData; error?: string }> {
    try {
      const newMaterial: UserMaterialData = {
        ...material,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Transform to match IndexedDB schema (add userId field for indexing)
      const indexedDBMaterial = {
        ...newMaterial,
        userId: userId // Add this for the IndexedDB index
      };

      const result = await indexedDBService.add('userMaterials', indexedDBMaterial);
      
      if (result.success) {
        // Clear cache to force refresh
        this.clearCache(userId, 'materials');
        return { success: true, material: newMaterial };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create user material'
      };
    }
  }

  async updateUserMetric(userId: string, metricId: string, updates: Partial<UserMetricData>): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await indexedDBService.get('userMetrics', metricId);
      
      if (result.success && result.data) {
        const updatedMetric = {
          ...result.data,
          ...updates,
          updated_at: new Date().toISOString(),
          userId: userId // Ensure userId is set for indexing
        };

        const updateResult = await indexedDBService.update('userMetrics', updatedMetric);
        
        if (updateResult.success) {
          // Clear cache to force refresh
          this.clearCache(userId, 'metrics');
          return { success: true };
        } else {
          return { success: false, error: updateResult.error };
        }
      } else {
        return { success: false, error: 'Metric not found' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update user metric'
      };
    }
  }

  async updateUserMaterial(userId: string, materialId: string, updates: Partial<UserMaterialData>): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await indexedDBService.get('userMaterials', materialId);
      
      if (result.success && result.data) {
        const updatedMaterial = {
          ...result.data,
          ...updates,
          updated_at: new Date().toISOString(),
          userId: userId // Ensure userId is set for indexing
        };

        const updateResult = await indexedDBService.update('userMaterials', updatedMaterial);
        
        if (updateResult.success) {
          // Clear cache to force refresh
          this.clearCache(userId, 'materials');
          return { success: true };
        } else {
          return { success: false, error: updateResult.error };
        }
      } else {
        return { success: false, error: 'Material not found' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update user material'
      };
    }
  }

  async deleteUserMetric(userId: string, metricId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await indexedDBService.delete('userMetrics', metricId);
      
      if (result.success) {
        // Clear cache to force refresh
        this.clearCache(userId, 'metrics');
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete user metric'
      };
    }
  }

  async deleteUserMaterial(userId: string, materialId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await indexedDBService.delete('userMaterials', materialId);
      
      if (result.success) {
        // Clear cache to force refresh
        this.clearCache(userId, 'materials');
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete user material'
      };
    }
  }

  // Method to force refresh cache (useful for manual refresh)
  async refreshUserData(userId: string, dataType?: 'metrics' | 'materials'): Promise<{ success: boolean; error?: string }> {
    try {
      this.clearCache(userId, dataType);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to refresh user data'
      };
    }
  }

  // Method to clear all cache (useful for logout)
  clearAllCache(): void {
    this.metricsCache.clear();
    this.materialsCache.clear();
  }
}

export const enhancedOrganizationalDataService = EnhancedOrganizationalDataService.getInstance(); 